import React, { useEffect, useState, useRef, Component } from 'react';
type SalesNotificationsProps = {
  /** Only run on the route(s) where social proof should appear. */
  enabled: boolean;
  /**
   * Becomes true once VIDEO PLAYBACK reaches the social-proof start mark
   * (18 min). The notification loop begins when this flips true — so the cards
   * track how far into the video the viewer is, NOT wall-clock time. Demo mode
   * (?spdemo=1) starts immediately and ignores this.
   */
  armed?: boolean;
  /**
   * CONTACT VARIANT (/vsl-contact): swap the purchase social-proof for
   * "just contacted us" messaging and a WhatsApp icon instead of the bundle.
   */
  contactMode?: boolean;
  /**
   * Where the notification card is anchored on screen. Defaults to 'top'
   * (slides down from the top edge). '/demo' passes 'bottom' so the cards
   * rise from the bottom edge instead. 'above-cta' anchors the card to the
   * bottom-LEFT, above the floating footer CTA button, and slides it in
   * horizontally from the left (traditional "just purchased" toast style).
   */
  position?: 'top' | 'bottom' | 'above-cta';
  /**
   * ABOVE-CTA VARIANT ONLY: whether the floating footer cart bar is currently
   * visible. When true the toast lifts above the bar (bottom-[104px]); when
   * false it drops down near the bottom edge (bottom-6) — mirroring the
   * WhatsApp bubble's height shift. Defaults to true so callers that always
   * show their cart bar keep the lifted position.
   */
  ctaVisible?: boolean;
  /**
   * Fired ONCE each time a new notification card appears on screen. '/demo'
   * uses this to tick the scarcity "members claimed" meter up in lockstep with
   * the pop-ups, so every visible sale bumps the count. Purely a side-effect
   * hook — it does NOT change when/how the cards themselves render.
   */
  onShow?: () => void;
};
/* ------------------------------------------------------------------ *
 * Config
 * ------------------------------------------------------------------ */
const TOTAL = 22; // total notifications shown across the run
const VISIBLE_MS = 4000; // how long each card stays on screen
const LEAVE_MS = 420; // slide-out animation duration (matches salesOut keyframe)
// Cards appear 12s apart. That interval = visible time + slide-out + gap, so the
// gap is 12000 − 4000 − 420 = 7580ms to keep a true 12s cadence between appearances.
const GAP_MS = 12000 - VISIBLE_MS - LEAVE_MS; // empty gap before the next appears
// First card appears almost instantly once the loop arms (on /demo it's armed
// the moment the visitor scrolls the first video half out of view), then the
// 12s cadence takes over for every subsequent card.
const FIRST_DELAY_MS = 600;
// Real first names + last initial so each appearance is a clearly distinct
// person (no emojis). The deck guarantees a unique person per appearance.
const FIRST_NAMES = ['Carlos', 'María', 'José', 'Ana', 'Luis', 'Sofía', 'Miguel', 'Valentina', 'Jorge', 'Camila', 'Diego', 'Lucía', 'Andrés', 'Daniela', 'Fernando', 'Gabriela', 'Ricardo', 'Paola', 'Roberto', 'Mariana', 'Eduardo', 'Isabela', 'Héctor', 'Natalia', 'Pablo', 'Verónica'];
const LAST_INITIALS = ['G', 'R', 'M', 'S', 'L', 'P', 'C', 'T', 'V', 'H', 'A', 'F'];
const CITIES = ['Monterrey', 'Guadalajara', 'Ciudad de México', 'Puebla', 'Querétaro', 'Mérida', 'Tijuana', 'León', 'Cancún', 'Toluca', 'Bogotá', 'Medellín', 'Lima', 'Santiago', 'Buenos Aires', 'Quito', 'San Juan', 'Guayaquil', 'Córdoba', 'Cali', 'Hermosillo', 'Culiacán'];
const DETAILS = ['Acaba de asegurar su acceso', 'Se acaba de unir al programa', 'Acaba de obtener su acceso', 'Aseguró su lugar hace un momento', 'Se inscribió hace unos segundos'];
// CONTACT VARIANT copy: people reaching out for info instead of buying.
const CONTACT_DETAILS = ['Acaba de contactarnos', 'Acaba de escribirnos por WhatsApp', 'Pidió más información hace un momento', 'Se acaba de contactar con nosotros', 'Nos escribió hace unos segundos'];
const APP_NAME = '48 HRS AI CASH MACHINE';
/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */
type Notice = {
  id: number;
  name: string;
  lastInitial: string;
  city: string;
  detail: string;
};
// Deterministic PRNG so the order/schedule is stable across reloads for the
// same browser (seeded from the persisted first-visit timestamp).
function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = seed + 0x6d2b79f5 | 0;
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
// Stable shuffled list of indices [0..n-1], deterministic per seed.
function shuffleIndices(seed: number, n: number): number[] {
  const rng = mulberry32(seed);
  const arr = Array.from({
    length: n
  }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
// Each index maps to ONE unique person (distinct first name), so consecutive
// notifications never repeat a name or look like they're "cycling".
function buildNotice(start: number, index: number, contactMode = false): Notice {
  const seed = start % 2147483647;
  const nameOrder = shuffleIndices(seed, FIRST_NAMES.length);
  const cityOrder = shuffleIndices(seed + 777, CITIES.length);
  const rng = mulberry32(seed + index * 9973);
  const pool = contactMode ? CONTACT_DETAILS : DETAILS;
  return {
    id: index,
    name: FIRST_NAMES[nameOrder[index % FIRST_NAMES.length]],
    lastInitial: LAST_INITIALS[Math.floor(rng() * LAST_INITIALS.length)],
    city: CITIES[cityOrder[index % CITIES.length]],
    detail: pool[Math.floor(rng() * pool.length)]
  };
}
/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */
export function SalesNotifications({
  enabled,
  armed,
  contactMode = false,
  position = 'top',
  ctaVisible = true,
  onShow
}: SalesNotificationsProps) {
  const isBottom = position === 'bottom';
  const isAboveCta = position === 'above-cta';
  // Keep the latest onShow in a ref so the notification loop effect can call it
  // without re-running (and thus never re-triggers or duplicates the cards).
  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;
  const [active, setActive] = useState<Notice | null>(null);
  const [leaving, setLeaving] = useState(false);
  // Ensures the loop is kicked off only once per session.
  const startedRef = useRef(false);
  // All pending timers, cleared together on unmount so nothing leaks.
  const timersRef = useRef<number[]>([]);
  // PREVIEW MODE: add ?spdemo=1 to the URL to watch the notifications now from
  // ANY route, without needing to reach the 18-min playback mark.
  const [isDemo] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('spdemo') === '1';
    } catch {
      return false;
    }
  });
  const clearTimers = () => {
    timersRef.current.forEach(t => window.clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => {
    // Only run where social proof is allowed, and only once playback has armed
    // it (or immediately in demo mode). Each card tracks video progress: the
    // sequence starts the moment the viewer crosses the 18-min mark.
    if (!enabled && !isDemo || !armed && !isDemo) return;
    if (startedRef.current) return;
    startedRef.current = true;
    // Fresh random order each session so two viewers don't see identical names.
    const seed = Date.now() % 2147483647 + 1;
    let index = 0;
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timersRef.current.push(id);
    };
    const showOne = () => {
      if (index >= TOTAL) return;
      const notice = buildNotice(seed, index, contactMode);
      index += 1;
      setLeaving(false);
      setActive(notice);
      // Notify listeners a fresh sale just popped (drives the scarcity meter).
      onShowRef.current?.();
      // Stay visible, then slide out, then leave a clean gap before the next.
      schedule(() => {
        setLeaving(true);
        schedule(() => {
          setActive(null);
          if (index < TOTAL) schedule(showOne, GAP_MS);
        }, LEAVE_MS);
      }, VISIBLE_MS);
    };
    // First card appears 10s after arming (600ms in demo mode for quick preview).
    schedule(showOne, isDemo ? 600 : FIRST_DELAY_MS);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isDemo, armed, contactMode]);
  const dismiss = () => {
    clearTimers();
    setLeaving(true);
    window.setTimeout(() => setActive(null), 420);
  };
  if (!enabled && !isDemo || !active) return null;
  // ABOVE-CTA VARIANT (/demo): a traditional "just purchased" toast anchored to
  // the bottom-LEFT, sitting above the floating footer CTA button, sliding in
  // horizontally from the left. Light card with the product photo.
  if (isAboveCta) {
    return <div className={`pointer-events-none fixed left-3 z-[60] flex justify-start px-0 transition-[bottom] duration-300 ease-out sm:left-5 ${ctaVisible ? 'bottom-[104px]' : 'bottom-6'}`} data-id="element-167" data-mp="381xbxh257n2i">
        <style data-id="element-168" data-mp="2z1imc5i5blt7">{`@keyframes salesInLeft{0%{transform:translateX(-120%);opacity:0}70%{transform:translateX(3%);opacity:1}100%{transform:translateX(0);opacity:1}}@keyframes salesOutLeft{0%{transform:translateX(0);opacity:1}100%{transform:translateX(-120%);opacity:0}}`}</style>
        <div role="status" aria-live="polite" onClick={dismiss} className="pointer-events-auto flex w-[300px] max-w-[calc(100vw-24px)] cursor-pointer select-none items-center gap-3 rounded-2xl border border-black/5 bg-white px-3 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.18)]" style={{
        animation: `${leaving ? 'salesOutLeft .42s cubic-bezier(.4,0,.2,1)' : 'salesInLeft .5s cubic-bezier(.22,1.2,.36,1)'} both`
      }} data-id="element-169" data-mp="2bczets6x7d26">
          {/* Product photo (or WhatsApp mark in contact mode) */}
          <span className={`relative flex h-[46px] w-[46px] shrink-0 items-center justify-center overflow-hidden rounded-xl ${contactMode ? 'bg-[#25D366]' : 'bg-[#0f172a]'}`} data-id="element-170" data-mp="3iylb392lsq">
            {contactMode ? <svg viewBox="0 0 24 24" className="h-[26px] w-[26px] fill-white" aria-hidden="true" data-id="element-171" data-mp="3ibgnqm8bigm1">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-172" data-mp="esf2qx26reo2" />
              </svg> : <img src="https://cdn.magicpatterns.com/uploads/14YUzBoLaDsTWifLWGFXe8/private_openrouter-generated-image-1781815370596.png" alt="" aria-hidden="true" className="h-full w-full object-cover" data-id="element-173" data-mp="1zbhhw1l6x4gk" />}
            {/* Small green check badge — classic "purchased" cue */}
            {!contactMode && <span className="absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white bg-[#16a34a]" data-id="element-174" data-mp="3ool04ebdp5h9">
                <svg viewBox="0 0 24 24" className="h-[11px] w-[11px] fill-none stroke-white" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-175" data-mp="3jsng9y0wpgzq">
                  <path d="M20 6 9 17l-5-5" data-id="element-176" data-mp="2ma4zkm1bci5o" />
                </svg>
              </span>}
          </span>

          <div className="min-w-0 flex-1" data-id="element-177" data-mp="2qecskvet0k9p">
            {/* Title: unique person (first name + last initial) + city */}
            <p className="truncate text-[13.5px] font-bold leading-tight text-[#111827]" data-id="element-178" data-mp="2vaa3gm7asyf3">
              {active.name} {active.lastInitial}. de {active.city}
            </p>
            {/* Body: contact detail in contact mode, product detail otherwise */}
            <p className="mt-0.5 truncate text-[12.5px] leading-tight text-[#4b5563]" data-id="element-179" data-mp="27ma6v5nb2dsl">
              {active.detail}
              {!contactMode && ' al 48 Hrs AI Cash Machine'}
            </p>
            {/* Time / verified footer line */}
            <p className="mt-0.5 truncate text-[11px] font-medium leading-tight text-[#9ca3af]" data-id="element-180" data-mp="1ewjx18sphy2a">
              Hace un momento · Compra verificada
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className={`pointer-events-none fixed inset-x-0 z-[140] flex justify-center px-3 ${isBottom ? 'bottom-3' : 'top-3'}`} data-id="element-181" data-mp="17f50s7p8cn6r">
      <style data-id="element-182" data-mp="12er39jl98vmz">{`@keyframes salesIn{0%{transform:translateY(-160%) scale(.96);opacity:0}55%{transform:translateY(6%) scale(1.01);opacity:1}80%{transform:translateY(-2%) scale(1)}100%{transform:translateY(0) scale(1);opacity:1}}@keyframes salesOut{0%{transform:translateY(0) scale(1);opacity:1}100%{transform:translateY(-160%) scale(.96);opacity:0}}@keyframes salesInBottom{0%{transform:translateY(160%) scale(.96);opacity:0}55%{transform:translateY(-6%) scale(1.01);opacity:1}80%{transform:translateY(2%) scale(1)}100%{transform:translateY(0) scale(1);opacity:1}}@keyframes salesOutBottom{0%{transform:translateY(0) scale(1);opacity:1}100%{transform:translateY(160%) scale(.96);opacity:0}}`}</style>
      <div role="status" aria-live="polite" onClick={dismiss} className="pointer-events-auto w-full max-w-[400px] cursor-pointer select-none rounded-[26px] px-4 py-3.5" style={{
      animation: `${leaving ? `${isBottom ? 'salesOutBottom' : 'salesOut'} .42s cubic-bezier(.4,0,.2,1)` : `${isBottom ? 'salesInBottom' : 'salesIn'} .5s cubic-bezier(.22,1.2,.36,1)`} both`,
      // iOS DARK-mode notification: smoky translucent dark glass.
      background: 'rgba(44,44,49,0.55)',
      backdropFilter: 'blur(30px) saturate(160%)',
      WebkitBackdropFilter: 'blur(30px) saturate(160%)',
      // Dark-glass DOUBLE edge: faint light inner ring + top sheen over a
      // soft dark outer definition and ambient drop shadow.
      boxShadow: ['0 18px 50px rgba(0,0,0,0.45)', '0 0 0 0.5px rgba(0,0,0,0.45)', 'inset 0 0 0 1px rgba(255,255,255,0.12)', 'inset 0 1px 0 rgba(255,255,255,0.20)' // top sheen
      ].join(',')
    }} data-id="element-183" data-mp="5p9rqnzlcfdd">
        <div className="flex items-start gap-3.5" data-id="element-184" data-mp="37ho49swf5f05">
          {/* App-icon tile — WhatsApp logo in contact mode, bundle otherwise */}
          <span className={`mt-0.5 flex h-[44px] w-[44px] shrink-0 items-center justify-center overflow-hidden rounded-[12px] ${contactMode ? 'bg-[#25D366]' : 'bg-[#0f172a]'}`} data-id="element-185" data-mp="3cdv89g598gfg">
            {contactMode ? <svg viewBox="0 0 24 24" className="h-[26px] w-[26px] fill-white" aria-hidden="true" data-id="element-186" data-mp="3hj8iamuxjna8">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-187" data-mp="3veazw795312v" />
              </svg> : <img src="https://cdn.magicpatterns.com/uploads/14YUzBoLaDsTWifLWGFXe8/private_openrouter-generated-image-1781815370596.png" alt="" aria-hidden="true" className="h-full w-full object-cover" data-id="element-188" data-mp="11upkot4xutw8" />}
          </span>

          <div className="min-w-0 flex-1" data-id="element-189" data-mp="1bl27ks086z5j">
            {/* Header row: app name + time, iOS style */}
            <div className="flex items-center justify-between gap-2" data-id="element-190" data-mp="1giapcrhgp169">
              <span className="truncate text-[12px] font-semibold uppercase tracking-[0.4px] text-white/55" data-id="element-191" data-mp="1liq1jfq0c2mn">
                {contactMode ? 'WHATSAPP' : APP_NAME}
              </span>
              <span className="shrink-0 text-[13px] text-white/40" data-id="element-192" data-mp="3a1awx2pw1fbn">ahora</span>
            </div>
            {/* Title: unique person (first name + last initial) + city */}
            <p className="mt-0.5 truncate text-[15px] font-semibold leading-tight text-white" data-id="element-193" data-mp="362bz022kqitp">
              {active.name} {active.lastInitial}. de {active.city}
            </p>
            {/* Body: contact detail in contact mode, product detail otherwise */}
            <p className="mt-0.5 truncate text-[14px] leading-tight text-white/65" data-id="element-194" data-mp="3to8otmx72vos">
              {active.detail}
              {!contactMode && ' al 48 Hrs AI Cash Machine'}
            </p>
          </div>
        </div>
      </div>
    </div>;
}